import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  Divider,
  TablePagination,
} from '@mui/material';
import { SmsHistoryResponse } from '@/types/sms';
import { formatErrorForDisplay } from '@/utils/errorUtils';
import { formatDateTimeIST } from '@/utils/dateUtils';

interface SmsHistoryModalProps {
  open: boolean;
  onClose: () => void;
  policyNumber: string;
  history: SmsHistoryResponse | null;
  loading: boolean;
  error: string | null;
}

const getSmsStatusColor = (status: string | null): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
  if (!status) return 'default';
  switch (status.toLowerCase()) {
    case 'sent':
      return 'success';
    case 'failed':
      return 'error';
    case 'pending':
      return 'warning';
    case 'queued':
      return 'info';
    default:
      return 'default';
  }
};


export function SmsHistoryModal({
  open,
  onClose,
  policyNumber,
  history,
  loading,
  error,
}: SmsHistoryModalProps) {
  const [page, setPage] = useState(0);
  const rowsPerPage = 5;

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const paginatedEvents = history
    ? history.recent_events.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
    : [];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      sx={{
        zIndex: 10000, // Higher than filter dropdowns (9999) to prevent overlap
      }}
      PaperProps={{
        sx: {
          maxHeight: '90vh',
        }
      }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">
            SMS History - {policyNumber}
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" p={4}>
            <CircularProgress />
            <Typography variant="body2" sx={{ ml: 2 }}>
              Loading SMS history...
            </Typography>
          </Box>
        ) : error ? (
          <Alert severity="error">{formatErrorForDisplay(error)}</Alert>
        ) : !history ? (
          <Typography color="text.secondary" align="center" sx={{ py: 4 }}>
            No history data available
          </Typography>
        ) : (
          <Box>
            {/* Summary Section */}
            <Box sx={{ mb: 3, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Summary
              </Typography>
              <Box display="flex" gap={3} flexWrap="wrap">
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Total Triggers
                  </Typography>
                  <Typography variant="h6" fontWeight="bold">
                    {history.total_triggers}
                  </Typography>
                </Box>
                {history.last_triggered_at && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Last Triggered
                    </Typography>
                    <Typography variant="body2" fontWeight="medium">
                      {formatDateTimeIST(history.last_triggered_at)}
                    </Typography>
                  </Box>
                )}
                {history.last_status && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Last Status
                    </Typography>
                    <Box sx={{ mt: 0.5 }}>
                      <Chip
                        label={history.last_status}
                        size="small"
                        color={getSmsStatusColor(history.last_status)}
                        variant="filled"
                      />
                    </Box>
                  </Box>
                )}
              </Box>
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* History Table */}
            {history.recent_events.length === 0 ? (
              <Typography color="text.secondary" align="center" sx={{ py: 4 }}>
                No SMS trigger history found
              </Typography>
            ) : (
              <Paper variant="outlined">
                <TableContainer>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>Date & Time</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Triggered By</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {paginatedEvents.map((event, index) => (
                        <TableRow key={index} hover>
                          <TableCell>
                            <Typography variant="body2" fontFamily="monospace">
                              {formatDateTimeIST(event.triggered_at)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {event.triggered_by || 'N/A'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={event.status || 'N/A'}
                              size="small"
                              color={getSmsStatusColor(event.status)}
                              variant="filled"
                            />
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={event.trigger_type || 'N/A'}
                              size="small"
                              variant="outlined"
                              color="primary"
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <TablePagination
                  component="div"
                  rowsPerPageOptions={[rowsPerPage]}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  count={history.recent_events.length}
                />
              </Paper>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}

